import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { Toaster } from "@/components/ui/sonner";
import { LOGO_URL } from "@/lib/constants";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import BookingSuccess from "@/pages/BookingSuccess";

import CRMLayout from "@/pages/crm/CRMLayout";
import Dashboard from "@/pages/crm/Dashboard";
import Requests from "@/pages/crm/Requests";
import Quotes from "@/pages/crm/Quotes";
import Invoices from "@/pages/crm/Invoices";
import Appointments from "@/pages/crm/Appointments";
import Customers from "@/pages/crm/Customers";
import Employees from "@/pages/crm/Employees";
import FAQPage from "@/pages/crm/FAQ";
import Templates from "@/pages/crm/Templates";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C6B5D]">
        Lade…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "Blessing Dienstleistungen – Alles rund ums Haus";

    const ensureLink = (rel, extra = {}) => {
      let el = document.querySelector(`link[rel='${rel}']`);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
      el.href = LOGO_URL;
    };
    ensureLink("icon", { type: "image/png" });
    ensureLink("apple-touch-icon");
    ensureLink("shortcut icon", { type: "image/png" });

    // If any third-party script overrides the title/icon later, restore ours.
    const observer = new MutationObserver(() => {
      if (document.title !== "Blessing Dienstleistungen – Alles rund ums Haus") {
        document.title = "Blessing Dienstleistungen – Alles rund ums Haus";
      }
    });
    observer.observe(document.querySelector("title") || document.head, {
      childList: true, subtree: true, characterData: true,
    });
    return () => observer.disconnect();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/booking-success" element={<BookingSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/crm"
            element={<Protected><CRMLayout /></Protected>}
          >
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="anfragen" element={<Requests />} />
            <Route path="angebote" element={<Quotes />} />
            <Route path="rechnungen" element={<Invoices />} />
            <Route path="termine" element={<Appointments />} />
            <Route path="kunden" element={<Customers />} />
            <Route path="mitarbeiter" element={<Employees />} />
            <Route path="wissensbasis" element={<FAQPage />} />
            <Route path="vorlagen" element={<Templates />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
